"use client";

import Link from "next/link";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from "lucide-react";

export type ToastTone = "success" | "info" | "warning" | "error";
export type ToastInput = {
  title: string;
  message?: string;
  tone?: ToastTone;
  href?: string;
  actionLabel?: string;
  duration?: number;
  // Toasts sharing a key replace each other instead of stacking.
  key?: string;
};
type Toast = ToastInput & { id: number; tone: ToastTone; leaving?: boolean };

const TOAST_EVENT = "velnqo:toast";
const MAX_TOASTS = 5;
const icons = { success: CheckCircle2, info: Info, warning: AlertTriangle, error: XCircle };

// Usable from anywhere, including non-React code: toast({ title: "Saved" }).
export function toast(input: ToastInput) {
  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent<ToastInput>(TOAST_EVENT, { detail: input }));
}

const ToastContext = createContext<(input: ToastInput) => void>(toast);
export const useToast = () => useContext(ToastContext);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer) clearTimeout(timer);
    timers.current.delete(id);
    setToasts((list) => list.map((item) => (item.id === id ? { ...item, leaving: true } : item)));
    setTimeout(() => setToasts((list) => list.filter((item) => item.id !== id)), 220);
  }, []);

  const schedule = useCallback((id: number, duration: number) => {
    const old = timers.current.get(id);
    if (old) clearTimeout(old);
    if (duration > 0) timers.current.set(id, setTimeout(() => dismiss(id), duration));
  }, [dismiss]);

  const push = useCallback((input: ToastInput) => {
    const id = nextId.current++;
    const tone = input.tone ?? "info";
    const duration = input.duration ?? (tone === "error" ? 9000 : 5500);
    setToasts((list) => {
      const kept = input.key ? list.filter((item) => item.key !== input.key) : list;
      return [...kept, { ...input, id, tone }].slice(-MAX_TOASTS);
    });
    schedule(id, duration);
  }, [schedule]);

  useEffect(() => {
    const listener = (event: Event) => push((event as CustomEvent<ToastInput>).detail);
    window.addEventListener(TOAST_EVENT, listener);
    const active = timers.current;
    return () => {
      window.removeEventListener(TOAST_EVENT, listener);
      active.forEach(clearTimeout);
      active.clear();
    };
  }, [push]);

  const value = useMemo(() => push, [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-viewport" role="region" aria-label="Notifications">
        {toasts.map((item) => {
          const Icon = icons[item.tone];
          return (
            <div
              key={item.id}
              className={`toast toast-${item.tone}${item.leaving ? " toast-leaving" : ""}`}
              role={item.tone === "error" || item.tone === "warning" ? "alert" : "status"}
              onMouseEnter={() => { const t = timers.current.get(item.id); if (t) { clearTimeout(t); timers.current.delete(item.id); } }}
              onMouseLeave={() => schedule(item.id, 2500)}
            >
              <Icon size={18} className="toast-icon" aria-hidden="true" />
              <div className="toast-body">
                <strong>{item.title}</strong>
                {item.message && <span>{item.message}</span>}
                {item.href && (
                  <Link href={item.href} onClick={() => dismiss(item.id)}>{item.actionLabel || "View"}</Link>
                )}
              </div>
              <button type="button" className="toast-close" aria-label="Dismiss notification" onClick={() => dismiss(item.id)}>
                <X size={14} />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}
